import type { Middleware } from '@reduxjs/toolkit';
import { LocalStorageKeys } from '@constants/enums';
import { AUTH_ACTIONS, TIMEOUTS } from '@constants/auth.constants';
import storageService from './storageService';
import authSvc from './authSvc';
import { isLegacyAuth } from './authUtils';
import { client } from '../config';
import { clearProfile } from '../../pages/profile/store';

let globalNavigate: ((path: string) => void) | null = null;

export const setNavigateFunction = (navigate: (path: string) => void) => {
  globalNavigate = navigate;
};

function handleLegacyAuthSideEffects(payload: any) {
  storageService.clearAuthData();

  localStorage.setItem(LocalStorageKeys.TOKEN, payload.token || '');
  localStorage.setItem(LocalStorageKeys.REFRESH, payload.refresh || '');
  localStorage.setItem(LocalStorageKeys.IS_KYC, String(payload.user_kyc));

  client.defaults.headers.common = {
    Authorization: `Bearer ${payload.token}`,
  };
}

function handleCognitoAuthSideEffects(payload: any) {
  localStorage.removeItem(LocalStorageKeys.IS_KYC);

  if (payload.idToken) {
    storageService.setIdToken(payload.idToken);
    localStorage.setItem(LocalStorageKeys.TOKEN, payload.idToken);
  }
  if (payload.accessToken) storageService.setAccessToken(payload.accessToken);
  if (payload.refreshToken) {
    storageService.setRefreshToken(payload.refreshToken);
    localStorage.setItem(LocalStorageKeys.REFRESH, payload.refreshToken);
  }
  if (payload.roles) storageService.setRoles(payload.roles);
  if (payload.permissions) storageService.setPermissions(payload.permissions);

  client.defaults.headers.common = {
    Authorization: `Bearer ${payload.idToken}`,
  };
}

function performLocalCleanup() {
  storageService.clearAuthData();
  localStorage.removeItem(LocalStorageKeys.TOKEN);
  localStorage.removeItem(LocalStorageKeys.REFRESH);
  localStorage.removeItem(LocalStorageKeys.IS_KYC);

  delete client.defaults.headers.common.Authorization;

  setTimeout(() => {
    if (globalNavigate) {
      globalNavigate('/');
    } else {
      window.location.href = '/';
    }
  }, TIMEOUTS.DEFAULT_TIMEOUT);
}

function handleLogoutSideEffects(stateBefore: any) {
  const authState = stateBefore?.auth;

  if (isLegacyAuth()) {
    if (authState?.refresh) {
      authSvc
        .tokenBlacklist({ refresh: authState.refresh })
        .catch(console.error);
    }
  } else {
    const accessTokenFromState = authState?.accessToken;
    const accessTokenFromStorage = storageService.getAccessToken();
    const accessToken = accessTokenFromStorage || accessTokenFromState;

    if (accessToken) {
      authSvc
        .cognitoLogout(accessToken)
        .catch((error) => {
          throw error;
        })
        .finally(() => {
          performLocalCleanup();
        });

      return;
    }
  }

  performLocalCleanup();
}

function handleLegacyTokenRefreshSideEffects(payload: any) {
  localStorage.removeItem(LocalStorageKeys.TOKEN);
  localStorage.setItem(LocalStorageKeys.TOKEN, payload.token || '');

  client.defaults.headers.common = {
    Authorization: `Bearer ${payload.token}`,
  };
}

function handleCognitoTokenRefreshSideEffects(payload: any) {
  storageService.setIdToken(payload.idToken);
  localStorage.setItem(LocalStorageKeys.TOKEN, payload.idToken);
  if (payload.accessToken) storageService.setAccessToken(payload.accessToken);
  if (payload.refreshToken) {
    storageService.setRefreshToken(payload.refreshToken);
    localStorage.setItem(LocalStorageKeys.REFRESH, payload.refreshToken);
  }

  client.defaults.headers.common = {
    Authorization: `Bearer ${payload.idToken}`,
  };
}

/**
 * Simple middleware to handle auth side effects
 */
const authMiddleware: Middleware = (store) => (next) => (action) => {
  const stateBefore =
    action.type === AUTH_ACTIONS.LOGOUT ? store.getState() : null;

  const result = next(action);

  switch (action.type) {
    case AUTH_ACTIONS.AUTHENTICATE:
      handleLegacyAuthSideEffects(action.payload);
      break;

    case AUTH_ACTIONS.COGNITO_AUTHENTICATE:
      handleCognitoAuthSideEffects(action.payload);
      break;

    case AUTH_ACTIONS.LOGOUT:
      store.dispatch(clearProfile());
      handleLogoutSideEffects(stateBefore);
      break;

    case AUTH_ACTIONS.LEGACY_TOKEN_REFRESHED:
      handleLegacyTokenRefreshSideEffects(action.payload);
      break;

    case AUTH_ACTIONS.COGNITO_TOKEN_REFRESHED:
      handleCognitoTokenRefreshSideEffects(action.payload);
      break;

    default:
      break;
  }

  return result;
};

export default authMiddleware;
